/**
 * Composes a query string from parameters.
 *
 * Converts an object of parameters into a URL-encoded query string.
 * - Functions are evaluated to get their return value
 * - undefined/null values result in `key=` (empty value)
 * - All values are URI-encoded
 *
 * @param params - Object with parameter keys and values
 * @returns URL-encoded query string (e.g., "key1=value1&key2=value2")
 */
export function composeQueryParams(params = {}) {
    return Object.keys(params).map((paramKey) => {
        let param = params[paramKey];
        if (param === undefined || param === null)
            return `${paramKey}=`;
        if (typeof param === 'function')
            param = param();
        return `${paramKey}=${encodeURIComponent(param)}`;
    }).join('&');
}
/**
 * Composes a complete URL with query parameters and optional hash.
 *
 * Combines a base URL with query parameters and/or a hash fragment.
 * Only adds query string if there are parameters. Hash is added if provided.
 *
 * @param url - Base URL
 * @param params - Query parameters object
 * @param hash - Optional hash fragment (will add '#' if not present)
 * @returns Complete URL with query string and/or hash
 *
 * @example
 * ```typescript
 * composeUrl('/path', {foo: 'bar'}, 'section1')
 * // Returns: '/path?foo=bar#section1'
 * ```
 */
export function composeUrl(url, params = {}, hash = '') {
    const queryAsEncodedString = composeQueryParams(params);
    if (queryAsEncodedString.length)
        return `${url}?${queryAsEncodedString}`;
    if (hash.length)
        return `${url}${hash.startsWith('#') ? hash : `#${hash}`}`;
    return url;
}
//# sourceMappingURL=query-params.js.map